import { Calendar, Clock, CheckCircle2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { usePreloadAvatars } from '@/hooks/usePreloadAvatars';
interface BookingSuccessScreenProps {
  open: boolean;
  onClose: () => void;
  shootingDate?: string;
  preparationDate?: string | 'pending';
  directorName?: string;
  directorAvatar?: string;
  pbmName?: string;
  pbmAvatar?: string;
}
export function BookingSuccessScreen({
  open,
  onClose,
  shootingDate,
  preparationDate,
  directorName,
  directorAvatar,
  pbmName,
  pbmAvatar
}: BookingSuccessScreenProps) {
  // Précharger les avatars
  usePreloadAvatars({
    directorAvatar,
    pbmAvatar
  });

  // Déterminer si on est en mode "en attente"
  const isPending = preparationDate === 'pending';

  // Format date pour affichage
  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formatTime = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    });
  };
  return <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="max-w-full w-full h-full p-0 border-0 bg-black z-[80] [&>button]:hidden">
        <VisuallyHidden>
          <DialogTitle>{isPending ? 'Demande envoyée' : 'Réservations confirmées'}</DialogTitle>
        </VisuallyHidden>
        
        {/* Main content centered */}
        <div className="h-full flex flex-col items-center justify-center px-6 py-20">
          <div className="max-w-lg w-full animate-fade-in">
            <div className="glass rounded-3xl px-8 py-10 text-center">
              {/* Success icon */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl" />
                  <CheckCircle2 className="h-16 w-16 text-green-400 relative" strokeWidth={1.5} />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-white text-xl font-semibold mb-8">
                {isPending ? 'Demande envoyée' : 'Réservations confirmées'}
              </h2>

              {/* Booking details */}
              <div className="space-y-4 mb-8">
                {/* RDV Préparation */}
                {preparationDate && <div className="glass rounded-2xl px-6 py-4 text-left">
                    <div className="flex items-start gap-4">
                      <div className="glass rounded-full p-2 mt-1">
                        {isPending ? <Clock className="h-4 w-4 text-white" /> : <Calendar className="h-4 w-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-white/50 text-xs mb-1">RDV Éditorial</p>
                        {isPending ? <>
                            <p className="text-white">En attente de confirmation</p>
                            <p className="text-white/70 text-sm mt-1">La conciergerie vous contactera sous 24h</p>
                          </> : <>
                            <p className="text-white capitalize">{formatDate(preparationDate)}</p>
                            <p className="text-white/70 text-sm mt-1">{formatTime(preparationDate)}</p>
                            {pbmName && <div className="mt-4 pt-4 border-t border-white/10">
                                <p className="text-white/50 text-xs mb-3">Votre Personal Brand Manager</p>
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-12 w-12">
                                    <AvatarImage src={pbmAvatar} alt={pbmName} />
                                    <AvatarFallback className="bg-primary/20 text-primary">
                                      {pbmName.split(' ').map(n => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-white font-medium">{pbmName}</span>
                                </div>
                              </div>}
                          </>}
                      </div>
                    </div>
                  </div>}

                {/* Tournage */}
                {shootingDate && <div className="glass rounded-2xl px-6 py-4 text-left">
                    <div className="flex items-start gap-4">
                      <div className="glass rounded-full p-2 mt-1">
                        <Calendar className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white/50 text-xs mb-1">Tournage</p>
                        <p className="text-white capitalize">{formatDate(shootingDate)}</p>
                        <p className="text-white/70 text-sm mt-1">{formatTime(shootingDate)}</p>
                        {directorName && <div className="mt-4 pt-4 border-t border-white/10">
                            <p className="text-white/50 text-xs mb-3">Votre Réalisateur</p>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-12 w-12">
                                <AvatarImage src={directorAvatar} alt={directorName} />
                                <AvatarFallback className="bg-primary/20 text-primary">
                                  {directorName.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-white font-medium">{directorName}</span>
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>}
              </div>

              {/* Confirmation button */}
              <button
                onClick={onClose}
                className="w-full glass hover:bg-white/10 text-white font-medium py-4 px-6 rounded-2xl transition-all duration-300"
              >
                Retourner au calendrier
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}